import { router } from '../router.js';

/**
 * The header a signed-OUT visitor sees: brand on the left, a three-bar menu on the right.
 *
 * The signed-in sidebar (components/navigation.js) is a different thing entirely and the two
 * are never on screen together — this one is drawn only when nobody is signed in, so it can
 * assume every link in it is public.
 *
 * Sign in lives here rather than on the landing page itself, which is what lets the landing
 * page be about the family instead of about a form.
 */

const LINKS = [
    { href: '/', icon: 'fa-house', label: 'Home' },
    { href: '/login', icon: 'fa-right-to-bracket', label: 'Sign in' },
    { href: '/privacy', icon: 'fa-shield-halved', label: 'Privacy policy' },
    { href: '/delete-account', icon: 'fa-trash-can', label: 'Delete your account' },
];

export function hidePublicNav() {
    document.querySelector('.public-nav')?.remove();
    document.body.classList.remove('has-public-nav');
}

export function renderPublicNav() {
    hidePublicNav();

    const path = router.getCurrentPath();
    const header = document.createElement('header');
    header.className = 'public-nav';
    header.innerHTML = `
        <a href="/" data-link class="public-nav-brand">
            <span class="public-nav-mark">S</span>
            <span>Shanks Family</span>
        </a>
        <button type="button" class="public-nav-toggle" aria-label="Menu" aria-expanded="false"
                aria-controls="public-nav-menu">
            <i class="fa-solid fa-bars"></i>
        </button>
        <nav id="public-nav-menu" class="public-nav-menu" hidden>
            ${LINKS.map((link) => `
                <a href="${link.href}" data-link class="public-nav-link${path === link.href ? ' active' : ''}">
                    <i class="fa-solid ${link.icon}"></i><span>${link.label}</span>
                </a>`).join('')}
        </nav>`;

    // First child, not appended: the header is `position: sticky`, and an element placed after
    // the page content sticks to the bottom of the scroll instead of the top of the window.
    document.body.prepend(header);
    document.body.classList.add('has-public-nav');

    const toggle = header.querySelector('.public-nav-toggle');
    const menu = header.querySelector('.public-nav-menu');

    const setOpen = (open) => {
        menu.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
    };

    toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        setOpen(menu.hidden);
    });

    // Any navigation closes it, including a click on the link that is already current --
    // otherwise the menu stays open over the page the visitor just asked for.
    menu.addEventListener('click', () => setOpen(false));

    // A click anywhere else closes it, which is what a visitor expects of a dropdown and
    // costs one listener. Bound on the document, so it also catches clicks on the page.
    document.addEventListener('click', (e) => {
        if (!menu.hidden && !header.contains(e.target)) setOpen(false);
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !menu.hidden) {
            setOpen(false);
            toggle.focus();
        }
    });
}
